import type { MapSettings } from "@glonextrack/contracts";

const envMapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID?.trim() || process.env.NEXT_PUBLIC_GOOGLE_MAP_ID?.trim();
const envApiKey =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY?.trim() ||
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY?.trim() ||
  process.env.NEXT_PUBLIC_MAPS_API_KEY?.trim();

const normalizeSettingValue = (value?: string | null): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export type GoogleMapsKeySource = "settings" | "env" | "none";

export interface ResolvedGoogleMapsBrowserKey {
  primaryKey: string;
  fallbackKey?: string;
  source: GoogleMapsKeySource;
}

export interface ResolvedGoogleMapsMapId {
  mapId?: string;
  source: "settings" | "env" | "none";
}

/**
 * Browser key precedence is settings-first so production runtime key changes
 * in Settings -> Maps take effect immediately without a redeploy.
 */
export const resolveGoogleMapsBrowserKey = (
  settingsApiKey?: string | null,
): ResolvedGoogleMapsBrowserKey => {
  const settingsKey = normalizeSettingValue(settingsApiKey);
  const envKey = normalizeSettingValue(envApiKey);

  if (settingsKey) {
    return {
      primaryKey: settingsKey,
      fallbackKey:
        envKey && envKey !== settingsKey ? envKey : undefined,
      source: "settings",
    };
  }

  if (envKey) {
    return {
      primaryKey: envKey,
      source: "env",
    };
  }

  return {
    primaryKey: "",
    source: "none",
  };
};

export const resolveGoogleMapsMapId = (
  settingsMapId?: string | null,
): ResolvedGoogleMapsMapId => {
  const settingsValue = normalizeSettingValue(settingsMapId);
  if (settingsValue) {
    return { mapId: settingsValue, source: "settings" };
  }
  const envValue = normalizeSettingValue(envMapId);
  if (envValue) {
    return { mapId: envValue, source: "env" };
  }
  return { mapId: undefined, source: "none" };
};

export const fallbackMapSettings: MapSettings = {
  provider: "google",
  theme: "standard",
  layers: {
    traffic: false,
    heatmap: false,
    geofences: true,
    cameras: false,
    incidents: false,
  },
  spatial: {
    clusters: true,
    isoLines: false,
    polygons: false,
    routeSnapping: true,
  },
  ai: {
    predictiveRouting: true,
    anomalyDetection: true,
    cameraSync: true,
  },
  iconSet: "smartFleet",
  assetDisplayMode: "standard",
  apiKey: envApiKey && envApiKey.length > 0 ? envApiKey : undefined,
  // Map IDs are project-scoped. Don't ship a hard-coded Map ID that may not exist
  // in the customer's Google Cloud project.
  mapId: envMapId && envMapId.length > 0 ? envMapId : undefined,
};

export const mergeMapSettings = (overrides?: Partial<MapSettings> | null): MapSettings => {
  return {
    // Google Maps is the sole supported provider; persisted "mapbox" settings
    // from retired tenants are coerced so they cannot select a removed engine.
    provider: "google",
    theme: overrides?.theme ?? fallbackMapSettings.theme,
    layers: {
      ...fallbackMapSettings.layers,
      ...(overrides?.layers ?? {}),
    },
    spatial: {
      ...fallbackMapSettings.spatial,
      ...(overrides?.spatial ?? {}),
    },
    ai: {
      ...fallbackMapSettings.ai,
      ...(overrides?.ai ?? {}),
    },
    iconSet: overrides?.iconSet ?? fallbackMapSettings.iconSet,
    assetDisplayMode: overrides?.assetDisplayMode ?? fallbackMapSettings.assetDisplayMode,
    apiKey: overrides?.apiKey ?? fallbackMapSettings.apiKey,
    mapId: overrides?.mapId?.trim?.() ? overrides.mapId.trim() : fallbackMapSettings.mapId,
    updatedAt: overrides?.updatedAt ?? fallbackMapSettings.updatedAt,
    updatedBy: overrides?.updatedBy ?? fallbackMapSettings.updatedBy,
  };
};


